/**
 * Analytics Service
 * Tracks key validation metrics to prove product value before monetization.
 */

import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  Timestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment
} from 'firebase/firestore';

export type EventType =
  | 'user_signup'
  | 'user_first_login'
  | 'user_return_visit'
  | 'user_session_start'
  | 'user_session_end'
  | 'conversation_created'
  | 'conversation_continued'
  | 'message_sent'
  | 'message_received'
  | 'conversation_depth_milestone'
  | 'roadmap_generated'
  | 'roadmap_viewed'
  | 'roadmap_started'
  | 'roadmap_day_completed'
  | 'roadmap_completed'
  | 'roadmap_abandoned'
  | 'daily_checkin'
  | 'activity_completed'
  | 'pet_name_mentioned'
  | 'memory_retrieval_success'
  | 'multi_language_switch'
  | 'roadmap_completion_celebration'
  | 'return_after_first_roadmap'
  | 'third_consecutive_day'
  | 'first_pet_behavior_success'
  | 'pet_profile_shared'
  | 'referral_link_generated'
  | 'recommendation_prompt_shown'
  | 'recommendation_prompt_accepted'
  | 'willingness_to_pay_survey'
  | 'feature_limit_reached'
  | 'premium_feature_interest'
  | 'cancel_consideration_question';

export interface AnalyticsEvent {
  userId: string;
  eventType: EventType;
  timestamp: Timestamp;
  sessionId?: string;
  language?: 'zh-CN' | 'es' | 'fr' | 'en';
  platform?: 'web' | 'mobile';
  metadata?: {
    conversationId?: string;
    roadmapId?: string;
    petName?: string;
    petType?: 'dog' | 'cat';
    messageCount?: number;
    dayNumber?: number;
    completionPercentage?: number;
    returnDaysCount?: number;
    consecutiveDays?: number;
    timeSpentSeconds?: number;
    [key: string]: any;
  };
}

export interface UserAnalyticsSummary {
  userId: string;
  totalSessions: number;
  totalMessagesent: number;
  totalConversations: number;
  totalRoadmapsGenerated: number;
  totalRoadmapsCompleted: number;
  firstLoginDate: Timestamp;
  lastLoginDate: Timestamp;
  totalReturnVisits: number;
  consecutiveDaysStreak: number;
  longestStreak: number;
  dailyCheckins: number;
  activitiesCompleted: number;
  roadmapCompletionRate: number;
  petNameMentions: number;
  conversationDepthAverage: number;
  hasReachedAhaMoment: boolean;
  ahaMomentDate?: Timestamp;
  petProfilesShared: number;
  referralLinksGenerated: number;
  recommendationWillingness?: number;
  featureLimitsReached: number;
  premiumInterestIndicators: number;
  estimatedWillingnessToPay?: number;
  lastUpdated: Timestamp;
  version: number;
}

class AnalyticsService {
  private sessionId: string | null = null;
  private sessionStartTime: Date | null = null;
  private lastActivityTime: Date | null = null;
  private SESSION_TIMEOUT_MINUTES = 30;
  
  constructor() {
    this.initializeSession();
  }
  
  private initializeSession() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date();
    this.lastActivityTime = new Date();
    this.setupSessionTimeout();
  }
  
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }
  
  private setupSessionTimeout() {
    setInterval(() => {
      if (this.lastActivityTime) {
        const minutesSinceLastActivity = 
          (new Date().getTime() - this.lastActivityTime.getTime()) / 1000 / 60;
        
        if (minutesSinceLastActivity > this.SESSION_TIMEOUT_MINUTES && this.sessionId) {
          this.endSession();
        }
      }
    }, 60000);
  }
  
  private updateLastActivity() {
    this.lastActivityTime = new Date();
  }
  
  public async startSession(userId: string, language?: 'zh-CN' | 'es' | 'fr' | 'en') {
    this.initializeSession();
    
    await this.trackEvent({
      userId,
      eventType: 'user_session_start',
      timestamp: Timestamp.now(),
      sessionId: this.sessionId!,
      language,
      metadata: {
        sessionId: this.sessionId!
      }
    });
  }
  
  public async endSession(userId?: string) {
    if (!this.sessionId || !this.sessionStartTime) return;
    
    const sessionDuration = Math.floor(
      (new Date().getTime() - this.sessionStartTime.getTime()) / 1000
    );
    
    if (userId) {
      await this.trackEvent({
        userId,
        eventType: 'user_session_end',
        timestamp: Timestamp.now(),
        sessionId: this.sessionId,
        metadata: {
          sessionDurationSeconds: sessionDuration
        }
      });
    }
    
    this.sessionId = null;
    this.sessionStartTime = null;
    this.lastActivityTime = null;
  }
  
  public async trackEvent(event: AnalyticsEvent): Promise<void> {
    try {
      this.updateLastActivity();
      
      await addDoc(collection(db, 'analyticsEvents'), {
        ...event,
        timestamp: event.timestamp || Timestamp.now(),
        sessionId: event.sessionId || this.sessionId
      });
      
      await this.updateUserSummary(event);
      
      console.log('📊 Analytics event tracked:', event.eventType);
    } catch (error) {
      console.error('❌ Error tracking analytics event:', error);
    }
  }
  
  private async updateUserSummary(event: AnalyticsEvent): Promise<void> {
    try {
      const summaryRef = doc(db, 'userAnalyticsSummaries', event.userId);
      const summarySnap = await getDoc(summaryRef);
      
      if (!summarySnap.exists()) {
        await this.createUserSummary(event.userId, event);
      } else {
        await this.incrementUserSummary(event);
      }
    } catch (error) {
      console.error('❌ Error updating user summary:', error);
    }
  }
  
  private async createUserSummary(userId: string, firstEvent: AnalyticsEvent): Promise<void> {
    const summary: UserAnalyticsSummary = {
      userId,
      totalSessions: 1,
      totalMessagesent: 0,
      totalConversations: 0,
      totalRoadmapsGenerated: 0,
      totalRoadmapsCompleted: 0,
      firstLoginDate: Timestamp.now(),
      lastLoginDate: Timestamp.now(),
      totalReturnVisits: 0,
      consecutiveDaysStreak: 1,
      longestStreak: 1,
      dailyCheckins: 0,
      activitiesCompleted: 0,
      roadmapCompletionRate: 0,
      petNameMentions: 0,
      conversationDepthAverage: 0,
      hasReachedAhaMoment: false,
      petProfilesShared: 0,
      referralLinksGenerated: 0,
      featureLimitsReached: 0,
      premiumInterestIndicators: 0,
      lastUpdated: Timestamp.now(),
      version: 1
    };
    
    await setDoc(doc(db, 'userAnalyticsSummaries', userId), summary);
  }
  
  private async incrementUserSummary(event: AnalyticsEvent): Promise<void> {
    const summaryRef = doc(db, 'userAnalyticsSummaries', event.userId);
    const updates: any = { lastUpdated: Timestamp.now() };
    
    switch (event.eventType) {
      case 'user_return_visit':
        updates.totalReturnVisits = increment(1);
        updates.lastLoginDate = Timestamp.now();
        break;
      case 'message_sent':
        updates.totalMessagesent = increment(1);
        break;
      case 'conversation_created':
        updates.totalConversations = increment(1);
        break;
      case 'roadmap_generated':
        updates.totalRoadmapsGenerated = increment(1);
        break;
      case 'roadmap_completed':
        updates.totalRoadmapsCompleted = increment(1);
        break;
      case 'daily_checkin':
        updates.dailyCheckins = increment(1);
        break;
      case 'activity_completed':
        updates.activitiesCompleted = increment(1);
        break;
      case 'pet_name_mentioned':
        updates.petNameMentions = increment(1);
        break;
      case 'pet_profile_shared':
        updates.petProfilesShared = increment(1);
        break;
      case 'referral_link_generated':
        updates.referralLinksGenerated = increment(1);
        break;
      case 'third_consecutive_day':
      case 'return_after_first_roadmap':
      case 'first_pet_behavior_success':
        updates.hasReachedAhaMoment = true;
        if (!updates.ahaMomentDate) {
          updates.ahaMomentDate = Timestamp.now();
        }
        break;
      case 'feature_limit_reached':
        updates.featureLimitsReached = increment(1);
        break;
      case 'premium_feature_interest':
        updates.premiumInterestIndicators = increment(1);
        break;
    }
    
    if (Object.keys(updates).length > 1) {
      await updateDoc(summaryRef, updates);
    }
  }
  
  public async trackSignup(userId: string, method: 'email' | 'google', language?: string) {
    await this.trackEvent({
      userId,
      eventType: 'user_signup',
      timestamp: Timestamp.now(),
      language: language as any,
      metadata: { signupMethod: method }
    });
  }
  
  public async trackReturnVisit(userId: string, daysSinceLastVisit: number) {
    await this.trackEvent({
      userId,
      eventType: 'user_return_visit',
      timestamp: Timestamp.now(),
      metadata: { daysSinceLastVisit }
    });
  }
  
  public async trackMessageSent(
    userId: string, 
    conversationId: string, 
    messageCount: number,
    containsPetName: boolean = false
  ) {
    await this.trackEvent({
      userId,
      eventType: 'message_sent',
      timestamp: Timestamp.now(),
      metadata: { conversationId, messageCount }
    });
    
    if ([5, 10, 20, 50, 100].includes(messageCount)) {
      await this.trackEvent({
        userId,
        eventType: 'conversation_depth_milestone',
        timestamp: Timestamp.now(),
        metadata: { conversationId, messageCount }
      });
    }
    
    if (containsPetName) {
      await this.trackEvent({
        userId,
        eventType: 'pet_name_mentioned',
        timestamp: Timestamp.now(),
        metadata: { conversationId }
      });
    }
  }
  
  public async trackRoadmapGenerated(
    userId: string,
    roadmapId: string,
    category: string,
    duration: number,
    petType?: 'dog' | 'cat',
    petName?: string
  ) {
    await this.trackEvent({
      userId,
      eventType: 'roadmap_generated',
      timestamp: Timestamp.now(),
      metadata: { 
        roadmapId, 
        category, 
        duration, 
        petType, 
        petName 
      }
    });
  }
  
  public async trackRoadmapDayCompleted(
    userId: string,
    roadmapId: string,
    dayNumber: number,
    completionPercentage: number,
    consecutiveDays: number
  ) {
    await this.trackEvent({
      userId,
      eventType: 'roadmap_day_completed',
      timestamp: Timestamp.now(),
      metadata: { roadmapId, dayNumber, completionPercentage, consecutiveDays }
    });
    
    if (consecutiveDays === 3) {
      await this.trackEvent({
        userId,
        eventType: 'third_consecutive_day',
        timestamp: Timestamp.now(),
        metadata: { roadmapId }
      });
    }
  }
  
  public async trackRoadmapCompleted(
    userId: string,
    roadmapId: string,
    totalDays: number,
    completionTimeSeconds: number
  ) {
    await this.trackEvent({
      userId,
      eventType: 'roadmap_completed',
      timestamp: Timestamp.now(),
      metadata: { roadmapId, totalDays, completionTimeSeconds }
    });
    
    await this.trackEvent({
      userId,
      eventType: 'roadmap_completion_celebration',
      timestamp: Timestamp.now(),
      metadata: { roadmapId }
    });
  }
  
  public async trackDailyCheckin(userId: string, consecutiveDays: number) {
    await this.trackEvent({
      userId,
      eventType: 'daily_checkin',
      timestamp: Timestamp.now(),
      metadata: { consecutiveDays }
    });
  }
  
  public async trackPetProfileShared(userId: string, petName: string, petType: 'dog' | 'cat') {
    await this.trackEvent({
      userId,
      eventType: 'pet_profile_shared',
      timestamp: Timestamp.now(),
      metadata: { petName, petType }
    });
  }
  
  public async getUserSummary(userId: string): Promise<UserAnalyticsSummary | null> {
    try {
      const summaryRef = doc(db, 'userAnalyticsSummaries', userId);
      const summarySnap = await getDoc(summaryRef);
      
      if (summarySnap.exists()) {
        return summarySnap.data() as UserAnalyticsSummary;
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting user summary:', error);
      return null;
    }
  }
  
  public async getUserEvents(
    userId: string,
    startDate: Date,
    endDate: Date
  ): Promise<AnalyticsEvent[]> {
    try {
      const eventsRef = collection(db, 'analyticsEvents');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate)),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as AnalyticsEvent);
    } catch (error) {
      console.error('❌ Error getting user events:', error);
      return [];
    }
  }
  
  public async calculateAhaMomentScore(userId: string): Promise<number> {
    try {
      const summary = await this.getUserSummary(userId);
      if (!summary) return 0;
      
      let score = 0;
      
      score += Math.min(summary.totalReturnVisits * 3, 30);
      score += Math.min(summary.totalRoadmapsCompleted * 5, 25);
      score += Math.min(summary.consecutiveDaysStreak * 2, 20);
      score += Math.min(summary.conversationDepthAverage, 15);
      score += Math.min(summary.petNameMentions, 10);
      
      return Math.min(Math.round(score), 100);
    } catch (error) {
      console.error('❌ Error calculating aha moment score:', error);
      return 0;
    }
  }
  
  public async getGlobalStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    totalRoadmaps: number;
    averageCompletionRate: number;
    totalMessages: number;
  }> {
    try {
      const summariesRef = collection(db, 'userAnalyticsSummaries');
      const querySnapshot = await getDocs(summariesRef);
      
      let totalUsers = 0;
      let activeUsers = 0;
      let totalRoadmaps = 0;
      let totalCompletedRoadmaps = 0;
      let totalMessages = 0;
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      querySnapshot.forEach(doc => {
        const summary = doc.data() as UserAnalyticsSummary;
        totalUsers++;
        totalRoadmaps += summary.totalRoadmapsGenerated;
        totalCompletedRoadmaps += summary.totalRoadmapsCompleted;
        totalMessages += summary.totalMessagesent;
        
        if (summary.lastLoginDate.toDate() > thirtyDaysAgo) {
          activeUsers++;
        }
      });
      
      const averageCompletionRate = 
        totalRoadmaps > 0 ? (totalCompletedRoadmaps / totalRoadmaps) * 100 : 0;
      
      return {
        totalUsers,
        activeUsers,
        totalRoadmaps,
        averageCompletionRate: Math.round(averageCompletionRate * 10) / 10,
        totalMessages
      };
    } catch (error) {
      console.error('❌ Error getting global stats:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        totalRoadmaps: 0,
        averageCompletionRate: 0,
        totalMessages: 0
      };
    }
  }
}

export const analyticsService = new AnalyticsService();

